import { Fragment } from 'react'
import { NavLink } from 'react-router-dom'
import { Menu, Transition } from '@headlessui/react'
import {
  Bars3Icon,
  BellIcon,
  ChevronDownIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  CreditCardIcon,
  ClipboardDocumentListIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline'

/**
 * Header Component
 *
 * Top navigation bar with user menu and notifications
 */
function Header({ setSidebarOpen, showSidebarToggle = true, user, onLogout }) {
  const isResident = user?.role === 'resident'

  const residentNavItems = [
    {
      name: 'Wallet',
      to: '/wallet',
      icon: CreditCardIcon,
    },
    {
      name: 'Ayuda',
      to: '/resident-ayuda',
      icon: ClipboardDocumentListIcon,
    },
    {
      name: 'Profile',
      to: '/resident-profile',
      icon: IdentificationIcon,
    },
  ]

  const userMenuItems = [
    {
      name: 'Profile',
      icon: UserCircleIcon,
      action: () => console.log('Profile clicked'),
    },
    {
      name: 'Settings',
      icon: Cog6ToothIcon,
      action: () => console.log('Settings clicked'),
    },
    {
      name: 'Sign out',
      icon: ArrowRightOnRectangleIcon,
      action: onLogout,
    },
  ]

  const getRoleBadge = (role) => {
    const badges = {
      admin: 'bg-red-100 text-red-800',
      barangay_official: 'bg-blue-100 text-blue-800',
      resident: 'bg-green-100 text-green-800',
    }
    return badges[role] || 'bg-gray-100 text-gray-800'
  }

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrator',
      barangay_official: 'Barangay Official',
      resident: 'Resident',
    }
    return labels[role] || role
  }

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="flex items-center justify-between px-6 py-4">
        {/* Left side - Mobile menu button */}
        <div className="flex items-center">
          {showSidebarToggle && (
            <button
              type="button"
              className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon className="h-6 w-6" />
            </button>
          )}

          {/* Breadcrumb or page title could go here */}
          {isResident ? (
            <NavLink to="/" className="ml-4 md:ml-0 flex items-center space-x-4">
              <div className="h-12 w-12 rounded-xl bg-primary-600 flex items-center justify-center">
                <span className="text-white font-bold">B360</span>
              </div>
              <div>
                <div className="text-xl font-semibold text-gray-900">Barangay 360</div>
                <div className="text-sm text-gray-500">Management System</div>
              </div>
            </NavLink>
          ) : (
            <div className="ml-4 md:ml-0">
              <h1 className="text-lg font-semibold text-gray-900">
                Welcome back, {user?.name}!
              </h1>
              <p className="text-sm text-gray-500">
                {new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          )}
        </div>

        {isResident && (
          <nav className="hidden lg:flex items-center space-x-4">
            {residentNavItems.map((item) => (
              <NavLink
                key={item.name}
                to={item.to}
                className={({ isActive }) =>
                  `inline-flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`
                }
              >
                <item.icon className="h-5 w-5" />
                <span>{item.name}</span>
              </NavLink>
            ))}
          </nav>
        )}

        {/* Right side - Notifications and user menu */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <button
            type="button"
            className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <span className="sr-only">View notifications</span>
            <BellIcon className="h-6 w-6" />
          </button>

          {/* User menu */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center space-x-3 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 p-2 hover:bg-gray-100">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  {user?.profile_photo ? (
                    <img
                      className="h-8 w-8 rounded-full object-cover"
                      src={user.profile_photo}
                      alt={user.name}
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center">
                      <span className="text-sm font-medium text-white">
                        {user?.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="hidden md:block text-left">
                  <p className="font-medium text-gray-900">{user?.name}</p>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadge(user?.role)}`}>
                      {getRoleLabel(user?.role)}
                    </span>
                  </div>
                </div>
                <ChevronDownIcon className="hidden md:block h-4 w-4 text-gray-400" />
              </div>
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  {/* User info (mobile) */}
                  <div className="md:hidden px-4 py-3 border-b border-gray-100">
                    <p className="font-medium text-gray-900">{user?.name}</p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${getRoleBadge(user?.role)}`}>
                      {getRoleLabel(user?.role)}
                    </span>
                  </div>

                  {/* Menu items */}
                  {userMenuItems.map((item) => (
                    <Menu.Item key={item.name}>
                      {({ active }) => (
                        <button
                          onClick={item.action}
                          className={`
                            ${active ? 'bg-gray-100' : ''}
                            group flex w-full items-center px-4 py-2 text-sm text-gray-700
                          `}
                        >
                          <item.icon
                            className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                            aria-hidden="true"
                          />
                          {item.name}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </header>
  )
}

export default Header
